import React, { useState, useRef, useEffect } from 'react';
import { FaExternalLinkSquareAlt, FaUpload } from 'react-icons/fa';
import "../../styles/Doctor-ui/Doctor-appointment/DA-results.css";
import { API_DETAILS } from '../../App';
import axios from 'axios';
import { toast } from '../../utils/toast';
import { errorMessage } from '../../utils/errorMessage';
import { useSubmitGuard } from '../../utils/useSubmitGuard';
import { openInNewTab } from '../../utils/openInNewTab';

// Must match spring.servlet.multipart.max-file-size on the server. Checked here
// first so a provider hears why, instead of watching a request fail.
const MAX_FILE_BYTES = 20 * 1024 * 1024;

const MIME_BY_EXTENSION = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

// Types a browser can show in a tab. The rest are offered as a download.
const VIEWABLE = new Set(['pdf', 'png', 'jpg', 'jpeg']);

const extensionOf = (name) => (name.split('.').pop() || '').toLowerCase();

/*
 * A patient's lab results, on the provider's chart.
 *
 * Choosing a file only ever added it to this list in the browser: the upload
 * function existed but nothing called it, so the result was gone on reload and
 * the patient never received it, while the provider saw it listed as though it
 * had been delivered. Files are now sent to the server as they are chosen, and
 * the list shows what the server holds.
 */
const FileUpload = (props) => {
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [uploading, guard] = useSubmitGuard();
  const fileInputRef = useRef();

  const resultsUrl = (path) =>
    API_DETAILS.baseUrl + API_DETAILS.PORT + API_DETAILS.baseExtension + path;

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    e.target.value = '';
    if (files.length === 0) return;

    const tooBig = files.find((file) => file.size > MAX_FILE_BYTES);
    if (tooBig) {
      toast.error(`"${tooBig.name}" is larger than 20 MB. Please upload a smaller copy.`);
      return;
    }
    uploadResults(files);
  };

  const uploadResults = guard(async (files) => {
    const form = new FormData();
    files.forEach((file) => form.append('files', file));
    form.append('patientId', props.data);
    try {
      // No Content-Type header: the browser has to write the multipart boundary
      // into it, and a hand-set value leaves the boundary out.
      const res = await axios.post(resultsUrl('/uploadResults'), form);
      setUploadedDocuments(res.data);
      toast.success(files.length === 1 ? 'Result uploaded.' : `${files.length} results uploaded.`);
    } catch (err) {
      toast.error(errorMessage(err, "We couldn't upload that result. Nothing was saved, so please try again."));
    }
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.data]);

  const fetchData = async () => {
    try {
      const res = await axios.get(resultsUrl(`/getResults/${encodeURIComponent(props.data)}`));
      setUploadedDocuments(res.data);
    } catch (err) {
      toast.error(errorMessage(err, "We couldn't load this patient's results. Refresh the page to try again."));
    }
  };

  // The server sends each file's bytes as base64. The old code wrapped the whole
  // response object in a Blob and labelled it a PDF, so nothing ever opened.
  const openDocument = (doc) => {
    const ext = extensionOf(doc.fileName);
    const binary = atob(doc.data);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const blob = new Blob([bytes], { type: MIME_BY_EXTENSION[ext] || 'application/octet-stream' });
    const url = URL.createObjectURL(blob);

    if (VIEWABLE.has(ext)) {
      if (!openInNewTab(url)) {
        toast.error('Your browser blocked the new tab. Allow pop-ups for Youro and try again.');
      }
    } else {
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.fileName;
      link.click();
    }
    // Long enough for the new tab to load it.
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  return (
    <div className="document-uploader">
      <div className="fu">
        <label htmlFor="file-upload" className="upload-label" aria-disabled={uploading}>
          <FaUpload />
          {uploading ? 'Uploading…' : 'Upload new result'}
        </label>
      </div>
      <input
        type="file"
        id="file-upload"
        accept=".jpg, .jpeg, .png, .pdf, .doc, .docx, .xls, .xlsx"
        ref={fileInputRef}
        onChange={handleFileSelect}
        disabled={uploading}
        multiple
        style={{ display: 'none' }}
      />

      {uploadedDocuments.length > 0 ? (
        <div>
          {uploadedDocuments.map((doc) => (
            <div className="document-list" key={doc.fileName}>
              <p>{doc.fileName}</p>
              <button type="button" className="pdf-open" onClick={() => openDocument(doc)}>
                <FaExternalLinkSquareAlt aria-hidden="true" />
                Open
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="document-empty">No results uploaded yet.</p>
      )}
    </div>
  );
};

export default FileUpload;
